import React from 'react';
import DashboardModal from '@/components/dashboard-modal';
import LoadingProgress, { Task } from './LoadingProgress';
import LoadingError from './LoadingError';
import LoadingSuccess from './LoadingSuccess';
import { AnimatePresence, motion } from 'framer-motion';

export type ProcessingState = 'processing' | 'error' | 'success';

interface ProcessingModalProps {
  isOpen: boolean;
  onClose: () => void;
  state: ProcessingState;
  tasks: Task[];
  progress: number;
  currentTaskDescription: string;
  errorMessage: string;
  onRetry: () => void;
  successMessage: string;
  successActionText: string;
  onSuccessAction: () => void;
  title?: string;
  description?: string;
}

const ProcessingModal: React.FC<ProcessingModalProps> = ({
  isOpen,
  onClose,
  state,
  tasks,
  progress,
  currentTaskDescription,
  errorMessage,
  onRetry,
  successMessage,
  successActionText,
  onSuccessAction,
  title = "Processing Request",
  description = "Please wait while we work our magic..."
}) => {
  const variants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  };

  return (
    <DashboardModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="lg"
      accentColor="purple"
      preventCloseOnUnsavedChanges={state === 'processing'}
    >
      <AnimatePresence mode="wait">
        {state === 'processing' && (
          <motion.div key="processing" variants={variants} initial="hidden" animate="visible" exit="exit">
            <LoadingProgress
              tasks={tasks}
              progress={progress}
              currentTaskDescription={currentTaskDescription}
            />
          </motion.div>
        )}
        {state === 'error' && (
          <motion.div key="error" variants={variants} initial="hidden" animate="visible" exit="exit">
            <LoadingError errorMessage={errorMessage} onRetry={onRetry} onCancel={onClose} />
          </motion.div>
        )}
        {state === 'success' && (
          <motion.div key="success" variants={variants} initial="hidden" animate="visible" exit="exit">
            <LoadingSuccess
              successMessage={successMessage}
              actionButtonText={successActionText}
              onActionClick={onSuccessAction}
              onClose={onClose}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </DashboardModal>
  );
};

export default ProcessingModal;
